// Shared html5-qrcode helper used by both barcode_scanner_controller and
// event_kiosk_controller. Centralizes the dynamic import + start/stop
// lifecycle so both controllers stay DRY and load the library only on demand.
//
// We keep the surface intentionally minimal — each controller retains its
// own UI (modal vs. inline region) and its own decoded-text handling.

export interface QrScannerHandle {
  stop: () => Promise<void>
}

export interface StartQrScannerOptions {
  elementId: string
  onDecode: (decodedText: string) => void
  onDecodeError?: (message: string) => void
  fps?: number
  /** Square decode region in px. 0 scans the whole frame and draws no box. */
  qrboxSize?: number
  aspectRatio?: number
}

// Starts a new scanner bound to `elementId`. The element must already exist
// in the DOM. Returns a handle whose `stop()` method ends the stream and
// releases the camera.
export async function startQrScanner(options: StartQrScannerOptions): Promise<QrScannerHandle> {
  const { elementId, onDecode, onDecodeError } = options
  const fps = options.fps ?? 10
  const qrboxSize = options.qrboxSize ?? 250
  const aspectRatio = options.aspectRatio ?? 1.0

  // Dynamic import keeps html5-qrcode (~250KB min+gz) out of the main bundle.
  const { Html5Qrcode } = await import("html5-qrcode")
  const scanner = new Html5Qrcode(elementId)

  await scanner.start(
    { facingMode: "environment" },
    {
      fps,
      // html5-qrcode draws its own white viewfinder around the qrbox, so a
      // surface that frames the shot itself passes 0 and takes the whole frame.
      ...(qrboxSize > 0 ? { qrbox: { width: qrboxSize, height: qrboxSize } } : {}),
      aspectRatio,
    },
    onDecode,
    (err: string) => {
      if (onDecodeError) onDecodeError(err)
    },
  )

  return {
    stop: async () => {
      try {
        await scanner.stop()
      } catch {
        /* no-op: scanner may already be stopped */
      }
    },
  }
}
